import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./trainers.css";

/* ---------- tiny reveal hook ---------- */
function useReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ---------- reusable UI bits ---------- */
const HighlightCircles = ({ items = [] }) => (
  <ul className="fact-circles" role="list">
    {items.map((t, i) => (
      <li className="fact-circle" key={i}>
        <span className="fact-circle__inner">{t}</span>
      </li>
    ))}
  </ul>
);

const FactGrid = ({ items = [] }) => (
  <dl className="fact-grid">
    {items.map(({ label, value }, i) => (
      <div className="fact-card" key={i}>
        <dt className="fact-card__label">{label}</dt>
        <dd className="fact-card__value">{value}</dd>
      </div>
    ))}
  </dl>
);

/* ===================================== */

export default function Trainers() {
  const heroVidRef = useRef(null);
  useReveal();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);
  useEffect(() => { const v = heroVidRef.current; if (v) v.playbackRate = 0.4; }, []);

  return (
    <main className="trainers-page">
      {/* ===== HERO ===== */}
      <section className="tr-hero">
        <div className="tr-media">
          <video
            ref={heroVidRef}
            className="tr-video"
            src="https://cosacademyvideos.s3.us-east-2.amazonaws.com/IMG_0936.mp4"
            playsInline muted autoPlay loop
          />
        </div>
        <div className="tr-veil" aria-hidden />
        <header className="tr-hero-inner elevate">
          <h1 className="tr-hero-title">Meet Our Master Trainers</h1>
        </header>
      </section>

      {/* ===== NIKKY ===== */}
      <section className="trainer-block reveal">
        <figure className="trainer-media">
          <img
            src="https://cosworksamples.s3.us-east-2.amazonaws.com/NikkyNew23.PNG"
            alt="Nikky Altemose, Master Artist & Educator"
            className="trainer-img"
            loading="lazy" decoding="async"
          />
        </figure>

        <article className="trainer-copy">
          <h2 className="trainer-name">Nikky Altemose</h2>
          <p className="trainer-role">Founder · Master Artist & Educator</p>

          {/* circular tabs */}
          <HighlightCircles
            items={[
              "12,000+ Procedures",
              "Master Trainer",
              "Aesthetician • Skin Anatomy",
            ]}
          />

          {/* compact grid facts */}
          <FactGrid
            items={[
              { label: "Specialty", value: "Hyper-realistic brows, refined symmetry" },
              { label: "Training", value: "European • Russian • Korean methods" },
              { label: "Mentorship", value: "Live models & ongoing support" },
            ]}
          />

          <p className="trainer-body">
            Nikky leads COSMETINK®’s elevated brow, lip, and eyeliner work.
            Her timeless shapes and precise approach define our aesthetic—techniques
            she passes directly to students through clear instruction and real-world practice.
          </p>
        </article>
      </section>

      {/* ===== SEAN ===== */}
      <section className="trainer-block trainer-block--reverse reveal">
        <figure className="trainer-media">
          <img
            src="https://cosworksamples.s3.us-east-2.amazonaws.com/SeanNew23a.PNG"
            alt="Sean Altemose, Master Artist & Educator"
            className="trainer-img"
            loading="lazy" decoding="async"
          />
        </figure>

        <article className="trainer-copy">
          <h2 className="trainer-name">Sean Altemose</h2>
          <p className="trainer-role">Co-Founder · Master Artist & Educator</p>

          <HighlightCircles
            items={[
              "B.S. Chemistry",
              "12+ Years Fashion",
              "4,000+ Procedures",
              "Master Trainer",
              "Skin Science + Anatomy",
            ]}
          />

          <FactGrid
            items={[
              { label: "Focus", value: "Natural enhancement with elegant precision" },
              { label: "Fashion Experience", value: "Modeled for clients such as Calvin Klein • Tommy Hilfiger • Levi’s • Saks" },
              { label: "Mentor", value: "Trained by Nikky Altemose" },
              { label: "Approach", value: "Balanced results that age gracefully" },
            ]}
          />

          <p className="trainer-body">
            Sean blends a science-driven understanding of skin with a fashion-honed eye.
            His mission is to emphasize what’s naturally unique in every face—delivering
            balanced, luxurious results.
          </p>
        </article>
      </section>

      {/* ===== CTA STRIP ===== */}
      <section className="tr-cta reveal" aria-label="Explore the Academy">
        <div className="tr-cta-inner">
          <h3 className="tr-cta-title">Train with COSMETINK®</h3>
          <p className="tr-cta-copy">Accredited education designed to launch new careers.</p>
          <div className="tr-cta-actions">
            <Link to="/courses/nano" className="tr-btn">Full Nano Brows</Link>
            <Link to="/courses/nanocombo" className="tr-btn">Nano Combo Brows</Link>
            <Link to="/courses/ombre" className="tr-btn">Ombré Brows</Link>
            <Link to="/courses/lips" className="tr-btn">Lip Blush</Link>
          </div>
          <br/><br/>
        </div>
      </section>
    </main>
  );
}
